import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, Search, Package, MoveRight, Loader2, X } from 'lucide-react';
import { AdminAPI, resolveImage } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { useSuppliers } from '../../hooks/useSuppliers';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import ImageUpload from './ImageUpload';
import RichTextEditor from './RichTextEditor';

const EMPTY = { name: '', brand: '', category: '', description: '', image: '', badge: '', variants: [] };
const EMPTY_VARIANT = { label: '', price: 0, supplier_id: '', stock: 0 };

const ProductsTab = ({ brands, categories, onRefresh }) => {
  const { toast } = useToast();
  const { byId: suppliersById } = useSuppliers();
  const suppliers = Object.values(suppliersById);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | productObj
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [moving, setMoving] = useState(null); // product being moved

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await AdminAPI.products({ q: q || undefined, category: filterCat || undefined, brand: filterBrand || undefined });
      setProducts(data);
    } finally { setLoading(false); }
  }, [q, filterCat, filterBrand]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing('new'); };
  const openEdit = (p) => {
    setForm({
      name: p.name || '', brand: p.brand || '', category: p.category || '',
      description: p.description || '', image: p.image || '', badge: p.badge || '',
      variants: (p.variants || []).map(v => ({
        label: v.label || '', price: v.price || 0, supplier_id: v.supplier_id || '', stock: v.stock || 0,
      })),
    });
    setEditing(p);
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  // Variant helpers
  const addVariant = () => setForm(f => ({ ...f, variants: [...(f.variants || []), { ...EMPTY_VARIANT }] }));
  const removeVariant = (idx) => setForm(f => ({ ...f, variants: f.variants.filter((_, i) => i !== idx) }));
  const updateVariant = (idx, key, value) => setForm(f => ({
    ...f,
    variants: f.variants.map((v, i) => i === idx ? { ...v, [key]: value } : v),
  }));

  const save = async () => {
    if (!form.name || !form.brand || !form.category) {
      toast({ title: 'Champs requis', description: 'Nom, marque et catégorie sont obligatoires' });
      return;
    }
    setBusy(true);
    try {
      // Nettoie les variantes : enlève celles vides, force types numériques
      const cleanVariants = (form.variants || [])
        .filter(v => v.label.trim() && v.supplier_id)
        .map(v => ({
          label: v.label.trim(),
          price: Number(v.price) || 0,
          supplier_id: v.supplier_id,
          stock: Number(v.stock) || 0,
        }));
      if (editing === 'new') {
        await AdminAPI.createProduct({ ...form, variants: cleanVariants });
        toast({ title: '✅ Produit créé' });
      } else {
        await AdminAPI.updateProduct(editing.id, { ...form, variants: cleanVariants });
        toast({ title: '✅ Produit mis à jour' });
      }
      close();
      load();
      onRefresh?.();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde échouée' });
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer "${p.name}" ?`)) return;
    try {
      await AdminAPI.deleteProduct(p.id);
      toast({ title: 'Produit supprimé' });
      load();
      onRefresh?.();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Suppression échouée' });
    }
  };

  const doMove = async (newCat) => {
    if (!moving || !newCat) { setMoving(null); return; }
    try {
      await AdminAPI.moveProduct(moving.id, newCat);
      toast({ title: '✅ Produit déplacé' });
      setMoving(null);
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Déplacement échoué' });
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7A6F5C]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un produit..." className="pl-9" data-testid="admin-products-search" />
        </div>
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="h-10 px-3 rounded-lg border border-[#E8E0D0] text-sm bg-white" data-testid="admin-products-filter-category">
          <option value="">Toutes les catégories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)} className="h-10 px-3 rounded-lg border border-[#E8E0D0] text-sm bg-white" data-testid="admin-products-filter-brand">
          <option value="">Toutes les marques</option>
          {brands.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
        </select>
        <Button onClick={openNew} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white whitespace-nowrap" data-testid="admin-products-new">
          <Plus className="w-4 h-4 mr-1" /> Nouveau produit
        </Button>
        <Button onClick={() => { setForm({ ...EMPTY, _masterMode: true }); setEditing('new'); }}
          variant="outline"
          className="border-[#1F4D3F] text-[#1F4D3F] hover:bg-[#FAF7F2] whitespace-nowrap"
          data-testid="admin-products-new-master"
          title="Crée une fiche produit sans prix. Les fournisseurs pourront ensuite y ajouter leur offre.">
          <Plus className="w-4 h-4 mr-1" /> Produit master (sans prix)
        </Button>
      </div>

      <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#7A6F5C]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement...</div>
        ) : products.length === 0 ? (
          <div className="p-10 text-center text-[#7A6F5C]"><Package className="w-10 h-10 mx-auto mb-3 opacity-50" /> Aucun produit</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F2] text-left text-[#7A6F5C] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Produit</th>
                <th className="px-4 py-3">Marque</th>
                <th className="px-4 py-3">Catégorie</th>
                <th className="px-4 py-3">Variantes</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-[#F5F1EA] hover:bg-[#FAF7F2]/40" data-testid={`admin-product-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.image && <img src={resolveImage(p.image)} alt="" className="w-10 h-10 rounded object-cover bg-[#FAF7F2]" onError={e => { e.target.style.visibility = 'hidden'; }} />}
                      <div>
                        <div className="font-medium text-[#2D2A26]">{p.name}</div>
                        <div className="text-xs text-[#7A6F5C] truncate max-w-[300px]">{p.description?.slice(0, 80)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#5A5247]">{p.brand}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded bg-[#F5F1EA] text-[#1F4D3F]">{categories.find(c => c.id === p.category)?.name || p.category}</span></td>
                  <td className="px-4 py-3 text-[#5A5247]">{p.variants?.length || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setMoving(p)} title="Déplacer" className="p-1.5 hover:bg-[#F5F1EA] rounded text-[#5A5247] mr-1" data-testid={`admin-product-move-${p.id}`}><MoveRight className="w-4 h-4" /></button>
                    <button onClick={() => openEdit(p)} title="Modifier" className="p-1.5 hover:bg-[#F5F1EA] rounded text-[#1F4D3F] mr-1" data-testid={`admin-product-edit-${p.id}`}><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(p)} title="Supprimer" className="p-1.5 hover:bg-rose-50 rounded text-rose-600" data-testid={`admin-product-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit / Create modal */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing === 'new'
                ? (form._masterMode ? 'Nouveau produit master (sans prix)' : 'Nouveau produit')
                : 'Modifier le produit'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            {form._masterMode && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900" data-testid="master-mode-banner">
                💡 <strong>Mode catalogue master</strong> — Vous créez la fiche produit (nom, marque, catégorie, photo, description) sans prix.
                Les fournisseurs verront ce produit en suggestion lorsqu'ils saisissent un nom similaire et pourront y ajouter leur offre en un clic.
                <br/><span className="text-xs">N'ajoutez pas de variantes ici — laissez les fournisseurs le faire.</span>
              </div>
            )}
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Nom *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-product-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Marque *</label>
                <select value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-[#E8E0D0] text-sm bg-white" data-testid="admin-product-form-brand">
                  <option value="">— Sélectionner —</option>
                  {brands.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Catégorie *</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-[#E8E0D0] text-sm bg-white" data-testid="admin-product-form-category">
                  <option value="">— Sélectionner —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Description</label>
              <RichTextEditor
                value={form.description}
                onChange={(html) => setForm({ ...form, description: html })}
                placeholder="Caractéristiques, indications, conditionnement..."
                testId="admin-product-form-description"
                minHeight={160}
              />
            </div>
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Badge (ex: NOUVEAU, BEST-SELLER)</label>
              <Input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="(optionnel)" />
            </div>
            <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} label="Image principale" testId="admin-product-form-image" />

            {/* Variantes — chaque variante = 1 offre fournisseur */}
            {!form._masterMode && (
            <div className="pt-3 border-t border-[#F5F1EA]">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-[#2D2A26]">Variantes & offres fournisseurs</label>
                <button type="button" onClick={addVariant} className="text-xs px-2 py-1 rounded border border-[#1F4D3F] text-[#1F4D3F] hover:bg-[#F5F1EA] inline-flex items-center gap-1" data-testid="admin-product-add-variant">
                  <Plus className="w-3 h-3" /> Ajouter
                </button>
              </div>
              {(!form.variants || form.variants.length === 0) ? (
                <div className="text-xs text-[#7A6F5C] bg-[#FAF7F2] rounded p-2 text-center">
                  Aucune variante. Le produit apparaîtra dans le catalogue mais sans prix tant qu'aucun fournisseur n'aura ajouté son offre.
                </div>
              ) : (
                <div className="space-y-2">
                  {form.variants.map((v, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_140px_110px_80px_30px] gap-2 items-center" data-testid={`admin-product-variant-row-${idx}`}>
                      <Input
                        value={v.label}
                        onChange={(e) => updateVariant(idx, 'label', e.target.value)}
                        placeholder="Ex: 4g — A1"
                        className="h-9 text-sm"
                      />
                      <select
                        value={v.supplier_id}
                        onChange={(e) => updateVariant(idx, 'supplier_id', e.target.value)}
                        className="h-9 px-2 rounded-lg border border-[#E8E0D0] text-xs bg-white"
                      >
                        <option value="">— Fournisseur —</option>
                        {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                      <Input
                        type="number" min="0"
                        value={v.price}
                        onChange={(e) => updateVariant(idx, 'price', e.target.value)}
                        placeholder="Prix DA"
                        className="h-9 text-sm"
                      />
                      <Input
                        type="number" min="0"
                        value={v.stock}
                        onChange={(e) => updateVariant(idx, 'stock', e.target.value)}
                        placeholder="Stock"
                        className="h-9 text-sm"
                      />
                      <button type="button" onClick={() => removeVariant(idx)} className="text-rose-600 hover:bg-rose-50 rounded p-1.5" title="Supprimer la variante">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <div className="text-xs text-[#7A6F5C] pt-1">
                    💡 Astuce : le prix minimum parmi les variantes est affiché en vitrine. Les fournisseurs peuvent aussi modifier leurs propres offres depuis leur tableau de bord.
                  </div>
                </div>
              )}
            </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={close}>Annuler</Button>
            <Button onClick={save} disabled={busy} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="admin-product-form-save">
              {busy ? 'Sauvegarde...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move dialog */}
      <Dialog open={moving !== null} onOpenChange={(o) => !o && setMoving(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Déplacer "{moving?.name}"</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <label className="text-sm text-[#2D2A26] block">Nouvelle catégorie</label>
            <select onChange={(e) => doMove(e.target.value)} defaultValue="" className="w-full h-10 px-3 rounded-lg border border-[#E8E0D0] bg-white text-sm">
              <option value="" disabled>Choisir...</option>
              {categories.filter(c => c.id !== moving?.category).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsTab;
